import { action, observable, makeObservable } from "mobx";
import { createIntl, createIntlCache } from "react-intl";
import { LOCALES_DATA } from "../../../i18n/locales";
import CookieStorage from "../../utility/cookie-storage";

const LONG_FORM_TO_BCP47: Record<string, string> = {
  english: "en-US",
  spanish: "es",
  chinese: "zh-CN",
  japanese: "ja",
  swedish: "sv",
};

const normalizeLang = (raw: string | null | undefined): string | null => {
  if (!raw) return null;
  const lower = raw.toLowerCase().trim();
  const mapped = LONG_FORM_TO_BCP47[lower] ?? lower;
  return LOCALES_DATA[mapped] ? mapped : null;
};


export default class LanguageStore {
  language = "en-US";
  messages = LOCALES_DATA[this.language];
  intl;
  constructor() {
    makeObservable(this, {
      language: observable,
      messages: observable,
      intl: observable,
      set: action,
      getText: action
    });

    this.language = this.getLangSetting();

    this.set(this.language);

    this.initParentMessageListener();
  }

  getMessages(language) {
    return LOCALES_DATA[language] ?? LOCALES_DATA["en-US"];
  }

  private initParentMessageListener = (): void => {
    if (typeof window === "undefined") return;

    window.addEventListener("message", (event: MessageEvent) => {
      const data = event?.data;
      if (!data || typeof data !== "object") return;

      const { action } = data;

      if (action === "set_language" || action === "language_change") {
        const raw = data.lang ?? data.language ?? data.value;
        const normalized = normalizeLang(raw);
        if (normalized && normalized !== this.language) {
          this.updateCookieAndSetLang(normalized);
        }
        return;
      }

      if (action === "recheck_language_cookies") {
        const fromParentCookie = normalizeLang(
          CookieStorage.get("lang_ck", false) ||
            CookieStorage.get("language", false)
        );
        if (fromParentCookie && fromParentCookie !== this.language) {
          this.updateCookieAndSetLang(fromParentCookie);
        }
      }
    });
  };

  getLangSetting = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = normalizeLang(params.get("lang") || params.get("show_lang"));
      if (fromUrl) {
        CookieStorage.set("BROADCASTER_LANG", fromUrl);
        return fromUrl;
      }
    } catch {}

    const existing = CookieStorage.get('BROADCASTER_LANG');
    if(existing) {
      return existing;
    }

    const fromParentCookie = normalizeLang(
      CookieStorage.get("lang_ck", false) || CookieStorage.get("language", false)
    );
    if (fromParentCookie) {
      CookieStorage.set("BROADCASTER_LANG", fromParentCookie);
      return fromParentCookie;
    }

    if(window.navigator.language.includes('en')) {
      return 'en-US';
    } else if(window.navigator.language.includes('es')) {
      return 'es';
    } else if(window.navigator.language.includes('zh')) {
      return 'zh-CN';
    } else if(window.navigator.language.includes('ja')) {
      return 'ja';
    } else if(window.navigator.language.includes('sv')) {
      return 'sv';
    } else {
      return 'en-US';
    }
  }

  updateCookieAndSetLang = (locale: string) => {
    if(LOCALES_DATA[locale]) {
      CookieStorage.set('BROADCASTER_LANG', locale);
      this.set(locale);
    }
  }

  set(locale) {
    this.language = locale;
    const cache = createIntlCache();
    this.intl = createIntl(
      {
        locale: locale,
        messages: LOCALES_DATA[locale],
      },
      cache
    );
  }

  getText(id, defaultText = "") {
    return this.intl.formatMessage({
      id: id,
      defaultMessage: defaultText,
    });
  }
}

export const languageStore = new LanguageStore();
